import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { addDays, subDays, format, isEqual, startOfDay, isToday } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import TutorialTooltip from '../common/TutorialTooltip';

// import 'swiper/swiper-bundle.css';

// Helper function to generate date range
const generateDateRange = (centerDate, range = 3) => {
    const dates = [];
    for (let i = -range; i <= range; i++) {
        dates.push(addDays(centerDate, i));
    }
    return dates;
};

function CalendarSlider({ selectedDate, onDateChange }) {

    const [visibleDates, setVisibleDates] = useState(generateDateRange(selectedDate));

    // Update visible dates when selectedDate changes externally
    useEffect(() => {
        setVisibleDates(generateDateRange(selectedDate));
        // Find the index of the selected date in the new range
        const selectedIndex = visibleDates.findIndex(date =>
            isEqual(startOfDay(date), startOfDay(selectedDate))
        );
        // Go to the slide if the swiper instance exists and the index is found
        if (swiperRef.current && swiperRef.current.swiper && selectedIndex !== -1) {
            swiperRef.current.swiper.slideTo(selectedIndex);
        }
    }, [selectedDate]); // Rerun when selectedDate prop changes


    const swiperRef = useRef(null);

    // Handler for clicking on a specific date slide
    const handleSlideClick = (date) => {
        onDateChange(date);
    };

    // Handler for previous day button
    const handlePrevDay = () => {
        const prevDay = subDays(selectedDate, 1);
        onDateChange(prevDay);
    };

    // Handler for next day button
    const handleNextDay = () => {
        const nextDay = addDays(selectedDate, 1);
        onDateChange(nextDay);
    };

    // Handler for date picker change
    const handleDatePickerChange = (date) => {
        if (date instanceof Date && !isNaN(date)) {
            onDateChange(date);
        }
    };

    const slides = visibleDates.map((date, index) => {
        const isSelected = isEqual(startOfDay(date), startOfDay(selectedDate));
        const isTodayDate = isToday(date);
        return (
            <SwiperSlide key={index} onClick={() => handleSlideClick(date)}>
                <div className={`slider-content ${isSelected ? 'selected' : ''} ${isTodayDate ? 'today' : ''}`}>
                    <div className="day">
                        {format(date, 'EEE')}
                    </div>
                    <div className="date">
                        {format(date, 'MMM d')}
                    </div>
                </div>
            </SwiperSlide>
        );
    });

    const params = {
        modules: [Navigation],
        simulateTouch: true,
        shortSwipes: true,
        speed: 400,
        slidesPerView: 3,
        spaceBetween: 14,
        breakpoints: {
            768: {
                slidesPerView: 5,
            },
            1140: {
                slidesPerView: 7,
            }
        }
    };

    return (
        <div className="days-slider">
            <TutorialTooltip
                id="match-schedule-tooltip"
                title="Match days"
                description="Click on the date to select one of the upcoming or past match days. Matches are played once in every two days."
                className="f-bottom"
            />
            <Swiper {...params} ref={swiperRef}>
                {slides}
            </Swiper>

            <div className="slider-ctrls">
                <div className="slider-nav">
                    <div className="nav-btn prev icon-arrow-left"
                         onClick={handlePrevDay}
                    />
                    <div className="nav-btn next icon-arrow-right"
                         onClick={handleNextDay}
                    />
                </div>
                <div className="calendar-col">
                    <DatePicker
                        selected={selectedDate}
                        onChange={handleDatePickerChange}
                        customInput={
                            <label htmlFor="date_picker_input" className="calendar-btn icon-calendar" />
                        }
                        dateFormat="MM.dd.yyyy"
                        id="date_picker_input"
                    />
                </div>
            </div>
        </div>
    );
}

export { CalendarSlider };